import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Star, Pencil, Trash2, Plus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", project: "", quote: "", rating: 5 };

export default function StoriesManager({ token }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // id or null (new)
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/testimonials`);
      setItems(data);
    } catch {
      toast.error("Failed to load stories.");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (t) => {
    setEditing(t.id);
    setForm({ name: t.name, project: t.project, quote: t.quote, rating: t.rating || 5 });
    setOpen(true);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim() || !form.quote.trim()) {
      toast.error("Name and quote are required.");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/testimonials/${editing}`, form, authHeader);
      } else {
        await axios.post(`${API}/testimonials`, form, authHeader);
      }
      toast.success("Story saved.");
      setOpen(false);
      await load();
    } catch {
      toast.error("Could not save story.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/testimonials/${id}`, authHeader);
      toast.success("Story deleted.");
      await load();
    } catch {
      toast.error("Could not delete story.");
    }
  };

  return (
    <div data-testid="stories-manager">
      <div className="flex items-center justify-between mb-6">
        <p className="text-slate-500">
          Click any story to edit it, or add a new homeowner review.
        </p>
        <Button
          onClick={openNew}
          data-testid="story-add-button"
          className="bg-orange-600 hover:bg-orange-700 text-white rounded-full"
        >
          <Plus size={16} className="mr-2" /> Add Story
        </Button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((t) => (
          <div
            key={t.id}
            data-testid={`story-card-${t.id}`}
            onClick={() => openEdit(t)}
            className="cursor-pointer bg-white border border-slate-100 rounded-2xl p-6 hover:shadow-md hover:border-blue-200 transition-all relative group"
          >
            <div className="flex gap-1 text-orange-500 mb-3">
              {[...Array(t.rating || 5)].map((_, s) => (
                <Star key={s} size={15} fill="currentColor" />
              ))}
            </div>
            <p className="text-slate-700 text-sm leading-relaxed line-clamp-4">"{t.quote}"</p>
            <div className="mt-4">
              <div className="font-semibold text-slate-900 text-sm">{t.name}</div>
              <div className="text-xs text-slate-500">{t.project}</div>
            </div>
            <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <span className="text-blue-600"><Pencil size={15} /></span>
              <button
                data-testid={`story-delete-${t.id}`}
                onClick={(e) => { e.stopPropagation(); remove(t.id); }}
                className="text-red-500 hover:text-red-700"
              >
                <Trash2 size={15} />
              </button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="story-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Story" : "Add Story"}</DialogTitle>
            <DialogDescription>
              These reviews appear in the "Homeowner Stories" section of your website.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="s-name">Homeowner name *</Label>
              <Input
                id="s-name"
                data-testid="story-name-input"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                placeholder="Marcus & Elena Reyes"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="s-project">Project / location</Label>
              <Input
                id="s-project"
                data-testid="story-project-input"
                value={form.project}
                onChange={(e) => set("project", e.target.value)}
                placeholder="Custom Home, Hillcrest"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="s-quote">Review *</Label>
              <Textarea
                id="s-quote"
                data-testid="story-quote-input"
                value={form.quote}
                onChange={(e) => set("quote", e.target.value)}
                rows={4}
                placeholder="Kairos turned our sketches into the home we'll grow old in…"
              />
            </div>
            <div className="space-y-2">
              <Label>Rating</Label>
              <div className="flex gap-1" data-testid="story-rating">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    data-testid={`story-rating-${n}`}
                    onClick={() => set("rating", n)}
                    className={n <= form.rating ? "text-orange-500" : "text-slate-300"}
                  >
                    <Star size={22} fill="currentColor" />
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">
              Cancel
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              data-testid="story-save-button"
              className="bg-orange-600 hover:bg-orange-700 text-white rounded-full"
            >
              {saving ? <Loader2 className="animate-spin" size={16} /> : "Save Story"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
